import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SocietyProvider } from "@/context/SocietyContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import Flats from "@/pages/Flats";
import FlatProfile from "@/pages/FlatProfile";
import Invoices from "@/pages/Invoices";
import Maintenance from "@/pages/Maintenance";
import Payments from "@/pages/Payments";
import Receipts from "@/pages/Receipts";
import Defaulters from "@/pages/Defaulters";
import MyReceipts from "@/pages/MyReceipts";
import FinancialReports from "@/pages/FinancialReports";
import WorkOrders from "@/pages/WorkOrders";
import PreventiveMaintenance from "@/pages/PreventiveMaintenance";
import Expenses from "@/pages/Expenses";
import Complaints from "@/pages/Complaints";
import Announcements from "@/pages/Announcements";
import Visitors from "@/pages/Visitors";
import QRScanner from "@/pages/QRScanner";
import VisitorLogs from "@/pages/VisitorLogs";
import GateSecurityLogin from "@/pages/GateSecurityLogin";
import Amenities from "@/pages/Amenities";
import Directory from "@/pages/Directory";
import MyFlat from "@/pages/MyFlat";
import InvoiceView from "@/pages/InvoiceView";
import MasterConsole from "@/pages/MasterConsole";
import Staff from "@/pages/Staff";
import MyVehicles from "@/pages/MyVehicles";
import Vehicles from "@/pages/Vehicles";
import ParkingManagement from "@/pages/ParkingManagement";
import VehicleLogs from "@/pages/VehicleLogs";
import RfidReaders from "@/pages/RfidReaders";
import ParkingAnalytics from "@/pages/ParkingAnalytics";
import DocumentTemplates from "@/pages/DocumentTemplates";
import DocumentGeneration from "@/pages/DocumentGeneration";
import DocumentLibrary from "@/pages/DocumentLibrary";
import UserProfile from "@/pages/UserProfile";
import Notifications from "@/pages/Notifications";
import VotingManagement from "@/pages/VotingManagement";
import VotingDashboard from "@/pages/VotingDashboard";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import DeleteAccount from "@/pages/DeleteAccount";
import "@/App.css";

function Protected({ children, roles }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-brand-bg">
                <div className="text-brand-inkSoft font-heading tracking-overline uppercase text-xs">
                    Loading maintayn...
                </div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (user.must_change_password) return <ForcePasswordChange />;
    if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
    return children;
}

function MasterProtected({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (user.kind !== "master") return <Navigate to="/app" replace />;
    return children;
}

function App() {
    return (
        <AuthProvider>
            <SocietyProvider>
                <BrowserRouter>
                    <Toaster position="top-center" offset="76px" />
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/delete-account" element={<DeleteAccount />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/gate-security" element={<GateSecurityLogin />} />
                        <Route path="/master" element={<MasterProtected><MasterConsole /></MasterProtected>} />
                        <Route path="/app" element={<Protected><Layout /></Protected>}>
                            <Route index element={<Dashboard />} />
                            <Route path="users" element={<Protected roles={["admin", "committee"]}><Users /></Protected>} />
                            <Route path="directory" element={<Directory />} />
                            <Route path="flats" element={<Protected roles={["admin", "committee"]}><Flats /></Protected>} />
                            <Route path="flats/:id" element={<Protected roles={["admin", "committee"]}><FlatProfile /></Protected>} />
                            <Route path="my-flat" element={<MyFlat />} />
                            <Route path="invoices" element={<Invoices />} />
                            <Route path="maintenance" element={<Protected roles={["admin", "committee"]}><Maintenance /></Protected>} />
                            <Route path="payments" element={<Protected roles={["admin", "committee"]}><Payments /></Protected>} />
                            <Route path="receipts" element={<Protected roles={["admin", "committee"]}><Receipts /></Protected>} />
                            <Route path="defaulters" element={<Protected roles={["admin", "committee"]}><Defaulters /></Protected>} />
                            <Route path="my-receipts" element={<Protected roles={["resident", "committee"]}><MyReceipts /></Protected>} />
                            <Route path="financial-reports" element={<Protected roles={["admin", "committee"]}><FinancialReports /></Protected>} />
                            <Route path="work-orders" element={<Protected roles={["admin", "committee"]}><WorkOrders /></Protected>} />
                            <Route path="preventive-maintenance" element={<Protected roles={["admin", "committee"]}><PreventiveMaintenance /></Protected>} />
                            <Route path="invoices/:id" element={<InvoiceView />} />
                            <Route path="expenses" element={<Expenses />} />
                            <Route path="complaints" element={<Complaints />} />
                            <Route path="staff" element={<Staff />} />
                            <Route path="announcements" element={<Announcements />} />
                            <Route path="visitors" element={<Protected roles={["committee", "resident"]}><Visitors /></Protected>} />
                            <Route path="qr-scanner" element={<QRScanner />} />
                            <Route path="visitor-logs" element={<VisitorLogs />} />
                            <Route path="my-vehicles" element={<MyVehicles />} />
                            <Route path="vehicles" element={<Protected roles={["admin", "committee"]}><Vehicles /></Protected>} />
                            <Route path="parking-management" element={<Protected roles={["admin", "committee"]}><ParkingManagement /></Protected>} />
                            <Route path="vehicle-logs" element={<VehicleLogs />} />
                            <Route path="rfid-readers" element={<Protected roles={["admin", "committee"]}><RfidReaders /></Protected>} />
                            <Route path="parking-analytics" element={<Protected roles={["admin", "committee"]}><ParkingAnalytics /></Protected>} />
                            <Route path="amenities" element={<Amenities />} />
                            <Route path="document-templates" element={<Protected roles={["admin", "committee"]}><DocumentTemplates /></Protected>} />
                            <Route path="document-generation" element={<Protected roles={["admin", "committee"]}><DocumentGeneration /></Protected>} />
                            <Route path="document-library" element={<DocumentLibrary />} />
                            <Route path="voting-management" element={<Protected roles={["admin", "committee"]}><VotingManagement /></Protected>} />
                            <Route path="voting" element={<VotingDashboard />} />
                            <Route path="profile" element={<UserProfile />} />
                            <Route path="notifications" element={<Notifications />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </SocietyProvider>
        </AuthProvider>
    );
}

export default App;
